import React from 'react';
import { ExpandContext } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import { useCallback, useMemo, useState } from 'react';
import { useStore } from '@xyflow/react';

export default function ExpandHandlerProvider({ children }) {
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [expandedSources, setExpandedSources] = useState({});
  const nodeLookup = useStore((store) => store.nodeLookup);

  /**
   * ISSUE: State Mutation in Expand Handler
   *
   * Problem: The original implementation used direct array mutation (expandedNodes.splice()),
   * which violates React's immutability principles and can cause:
   * - State updates not being detected by React
   * - Stale closures in callbacks
   * - Unpredictable re-render behavior
   *
   * Solution: Use functional state updates with immutable operations. This ensures:
   * - React properly detects state changes
   * - No stale closures
   * - Predictable re-render behavior
   * - Proper handling of secondary flow expansion (only one secondary flow expanded at a time)
   */
  const onExpandNode = useCallback(
    (flowNodeId) => {
      setExpandedNodes((prevExpandedNodes) => {
        if (prevExpandedNodes.includes(flowNodeId)) {
          // Collapse: remove the node from expanded list
          return prevExpandedNodes.filter((id) => id !== flowNodeId);
        } else {
          // Expand: add the node to expanded list
          // Check if this is a secondary flow node by looking at the node type
          const node = nodeLookup.get(flowNodeId);
          const isSecondaryFlow = node?.type === 'secondary_flow';

          if (isSecondaryFlow) {
            // For secondary flows, collapse all other secondary flows first
            // This ensures only one secondary flow is expanded at a time
            const filteredNodes = prevExpandedNodes.filter(
              (nodeId) => nodeLookup.get(nodeId)?.type !== 'secondary_flow',
            );
            return [...filteredNodes, flowNodeId];
          } else {
            // For regular flows, use normal behavior
            return [...prevExpandedNodes, flowNodeId];
          }
        }
      });
    },
    [nodeLookup],
  );

  const onExpandSource = useCallback(
    (flowNodeId) => {
      const newExpandedSources = { ...expandedSources };
      if (expandedSources[flowNodeId]) {
        delete newExpandedSources[flowNodeId];
      } else {
        newExpandedSources[flowNodeId] = true;
      }
      setExpandedSources(newExpandedSources);
    },
    [expandedSources],
  );

  const memoValue = useMemo(() => {
    return { expandedNodes, onExpandNode, expandedSources, onExpandSource };
  }, [onExpandNode, expandedNodes, expandedSources, onExpandSource]);

  return <ExpandContext.Provider value={memoValue}>{children}</ExpandContext.Provider>;
}
